/**
 * Main layout component with left navigation and global command palette.
 *
 * The CommandPalette is mounted here so it's accessible from every page (AC-021).
 * Cmd+K (Mac) / Ctrl+K (Windows) opens the palette (AC-014).
 */

import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { LeftNav } from './LeftNav';
import { UserMenu } from '../auth/UserMenu';
import { CommandPalette } from '../CommandPalette';

export function MainLayout() {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  // AC-014: Global keyboard shortcut for Cmd+K / Ctrl+K
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setIsCommandPaletteOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Navigation */}
      <LeftNav />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header with user menu */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Dossier Corporate Intelligence</span>
            {/* Cmd+K shortcut hint */}
            <button
              onClick={() => setIsCommandPaletteOpen(true)}
              className="hidden sm:flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <kbd className="font-sans">
                {navigator.platform?.includes('Mac') ? '\u2318' : 'Ctrl+'}
              </kbd>
              <kbd className="font-sans">K</kbd>
            </button>
          </div>
          <UserMenu />
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Command Palette — accessible from every page (AC-021) */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </div>
  );
}

export default MainLayout;
